<template>
  <div class="imports-data-table">
    <v-data-table
      v-if="loading || items != null"
      :headers="headers"
      :items="items || []"
      :items-per-page="items_per_page"
      :loading="loading"
      :loading-text="loading_text"
      :no-data-text="no_data_text"
      :footer-props="footer_props"
      class="enhanced-data-table enhanced-data-table--imports elevation-1"
    >
    <!-- Import search specific cells with sensible defaults; pages can override via cell-* slots -->
    <template v-slot:[`item.date`]="slotProps">
      <slot v-if="$scopedSlots['cell-date']" name="cell-date" v-bind="slotProps" />
      <span v-else>{{ $rc.pretty(slotProps.item.import_time,'date') }}</span>
    </template>
    <template v-slot:[`item.time`]="slotProps">
      <slot v-if="$scopedSlots['cell-time']" name="cell-time" v-bind="slotProps" />
      <span v-else>{{ $rc.pretty(slotProps.item.import_time,'time') }}</span>
    </template>
    <template v-slot:[`item.imported_by`]="slotProps">
      <slot v-if="$scopedSlots['cell-imported_by']" name="cell-imported_by" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.imported_by_employee_label }}</span>
    </template>
    <template v-slot:[`item.file_name`]="slotProps">
      <slot v-if="$scopedSlots['cell-file_name']" name="cell-file_name" v-bind="slotProps" />
      <a v-else href="#" @click.prevent="onClickDownloadFile(slotProps.item)" style="text-decoration: underline;">
        {{ slotProps.item.original_file_name }}
      </a>
    </template>
    <template v-slot:[`item.enriched_data`]="slotProps">
      <slot v-if="$scopedSlots['cell-enriched_data']" name="cell-enriched_data" v-bind="slotProps" />
      <a v-else-if="slotProps.item.enriched_data_blob_id" href="#" @click.prevent="onClickDownloadEnrichedData(slotProps.item)" style="text-decoration: underline;">
        Download Mapped File
      </a>
      <span v-else class="grey--text text--lighten-1">—</span>
    </template>
    <template v-slot:[`item.state`]="slotProps">
      <slot v-if="$scopedSlots['cell-state']" name="cell-state" v-bind="slotProps" />
      <v-chip v-else :color="getStatusColor(slotProps.item.state)" small text-color="white">
        {{ formatStatus(slotProps.item.state) }}
      </v-chip>
    </template>
    <template v-slot:[`item.import_data_source`]="slotProps">
      <slot v-if="$scopedSlots['cell-import_data_source']" name="cell-import_data_source" v-bind="slotProps" />
      <span v-else class="text-caption grey--text text--darken-1">
        {{ formatImportDataSource(slotProps.item.import_data_source) }}
      </span>
    </template>
    <template v-slot:[`item.import_execution_mode`]="slotProps">
      <slot v-if="$scopedSlots['cell-import_execution_mode']" name="cell-import_execution_mode" v-bind="slotProps" />
      <v-chip
        v-else
        small
        :color="getImportExecutionModeColor(slotProps.item.import_execution_mode)"
        text-color="white"
      >
        <v-icon small left>{{ (slotProps.item.import_execution_mode || '').toLowerCase() === 'remove' ? 'mdi-account-minus' : 'mdi-account-plus' }}</v-icon>
        {{ formatImportExecutionMode(slotProps.item.import_execution_mode) }}
      </v-chip>
    </template>
    <template v-slot:[`item.records_imported`]="slotProps">
      <slot v-if="$scopedSlots['cell-records_imported']" name="cell-records_imported" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.records_imported || 0 }}</span>
    </template>
    <template v-slot:[`item.list_members_added`]="slotProps">
      <slot v-if="$scopedSlots['cell-list_members_added']" name="cell-list_members_added" v-bind="slotProps" />
      <span v-else>{{ slotProps.item.list_members_added || slotProps.item.records_imported || 0 }}</span>
    </template>
    <template v-slot:[`item.recipients_added`]="slotProps">
      <slot v-if="$scopedSlots['cell-recipients_added']" name="cell-recipients_added" v-bind="slotProps" />
      <v-tooltip v-else-if="slotProps.item.state === 'completed'" bottom>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on" class="records-changed-cell">
            <v-icon small :color="getImportExecutionModeColor(slotProps.item.import_execution_mode)" class="mr-1">
              {{ isRemoveMode(slotProps.item.import_execution_mode) ? 'mdi-minus' : 'mdi-plus' }}
            </v-icon>
            <span :class="getRecordsChangedClass(slotProps.item.import_execution_mode)">
              {{ slotProps.item.recipients_added || slotProps.item.records_imported || 0 }}
            </span>
          </div>
        </template>
        <span>{{ isRemoveMode(slotProps.item.import_execution_mode) ? 'Recipients selected to be removed from this bulk message' : 'Recipients selected to be updated/added to this bulk message' }}</span>
      </v-tooltip>
      <v-tooltip v-else bottom>
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on" class="grey--text text--darken-1">N/A</span>
        </template>
        <span>Recipients count is available after import is completed</span>
      </v-tooltip>
    </template>
    <template v-slot:[`item.target_list_names`]="slotProps">
      <slot v-if="$scopedSlots['cell-target_list_names']" name="cell-target_list_names" v-bind="slotProps" />
      <div v-else class="target-lists-display">
        <v-tooltip bottom v-for="(target) in getTargetItems(slotProps.item, 'list').displayItems" :key="target.id">
          <template v-slot:activator="{ on, attrs }">
            <router-link
              :to="target.route"
              style="text-decoration: none;"
              v-bind="attrs"
              v-on="on"
            >
              <v-chip
                small
                color="purple"
                text-color="white"
                class="mr-1 mb-1 clickable-chip"
              >
                <v-icon small left>mdi-format-list-bulleted</v-icon>
                {{ truncateName(target.name) }}
              </v-chip>
            </router-link>
          </template>
          <span>{{ target.name }}</span>
        </v-tooltip>
        <v-chip
          v-if="getTargetItems(slotProps.item, 'list').overflowCount > 0"
          small
          outlined
          color="grey"
          class="mr-1 mb-1"
        >
          +{{ getTargetItems(slotProps.item, 'list').overflowCount }} more
        </v-chip>
        <span v-if="!slotProps.item.target_list_names || !slotProps.item.target_list_names.trim()" class="text-caption grey--text">
          No target lists
        </span>
      </div>
    </template>
    <template v-slot:[`item.target_bulk_message_names`]="slotProps">
      <slot v-if="$scopedSlots['cell-target_bulk_message_names']" name="cell-target_bulk_message_names" v-bind="slotProps" />
      <div v-else class="target-bulk-messages-display">
        <v-tooltip bottom v-for="(target) in getTargetItems(slotProps.item, 'bulk_message').displayItems" :key="target.id">
          <template v-slot:activator="{ on, attrs }">
            <router-link
              :to="target.route"
              style="text-decoration: none;"
              v-bind="attrs"
              v-on="on"
            >
              <v-chip
                small
                color="blue"
                text-color="white"
                class="mr-1 mb-1 clickable-chip"
              >
                <v-icon small left>mdi-email-multiple</v-icon>
                {{ truncateName(target.name) }}
              </v-chip>
            </router-link>
          </template>
          <span>{{ target.name }}</span>
        </v-tooltip>
        <v-chip
          v-if="getTargetItems(slotProps.item, 'bulk_message').overflowCount > 0"
          small
          outlined
          color="grey"
          class="mr-1 mb-1"
        >
          +{{ getTargetItems(slotProps.item, 'bulk_message').overflowCount }} more
        </v-chip>
        <span v-if="!slotProps.item.target_bulk_message_names || !slotProps.item.target_bulk_message_names.trim()" class="text-caption grey--text">
          No target bulk message
        </span>
      </div>
    </template>
    <template v-slot:[`item.actions`]="slotProps">
      <slot v-if="$scopedSlots['cell-actions']" name="cell-actions" v-bind="slotProps" />
      <div v-else>
        <v-menu
          v-if="shouldShowActionsMenu(slotProps.item)"
          :value="actions_menu_open_id === slotProps.item.import_id"
          :close-on-content-click="false"
          offset-y
          @input="setActionsMenuOpen(slotProps.item, $event)"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn small outlined v-bind="attrs" v-on="on">
              Actions
              <v-icon right small>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
             <v-list-item
              v-if="slotProps.item.state !== 'completed'"
              @click="onClickViewPreview(slotProps.item); closeActionsMenu(slotProps.item)"
            >
              <v-list-item-title>View Preview</v-list-item-title>
            </v-list-item>
            <import-details-dialog
              v-if="hasDetailsValue(slotProps.item.import_data_source_details)"
              :details="slotProps.item.import_data_source_details"
              activator_variant="list-item"
              button_text="View Import Source Technical Details"
              :hide_when_empty="true"
              @dialog-closed="closeActionsMenu(slotProps.item)"
            />

          </v-list>
        </v-menu>
        <span v-else class="text-caption grey--text text--lighten-1">-</span>
      </div>
    </template>
    </v-data-table>
  </div>
</template>

<script>
import ImportDetailsDialog from '@/components/imports/import-details-dialog.vue';

export default {
  name: 'ImportsDataTable',
  components: { ImportDetailsDialog },
  props: {
    headers: { type: Array, required: true },
    items: { type: Array, required: true },
    import_type: { type: String, required: true },
    upload_route_name: { type: String, required: true },
    items_per_page: { type: Number, default: 50 },
    loading: { type: Boolean, default: false },
    loading_text: { type: String, default: 'Loading imports...' },
    no_data_text: { type: String, default: 'No imports available' },
    footer_props: { type: Object, default: () => ({
      itemsPerPageText: 'Imports per page:',
      itemsPerPageOptions: [25, 50, 100],
      disableItemsPerPage: false,
      disablePagination: false
    }) }
  },
  methods: {
    formatStatus(status) {
      return this.$rc.pretty(status, 'enum_import_state');
    },
    getStatusColor(status) {
      switch (status) {
        case 'uploaded': return 'blue';
        case 'preview-ready': return 'orange';
        case 'completed': return 'success';
        case 'failed': return 'red';
        case 'cancelled': return 'grey';
        default: return 'grey';
      }
    },
    formatImportDataSource(source) {
      return this.$rc.pretty(source, 'enum_import_data_source');
    },
    formatImportExecutionMode(mode) {
      if (!mode) return 'Add';
      const modeLower = mode.toLowerCase();
      if (modeLower === 'remove') return 'Remove';
      return 'Add';
    },
    getImportExecutionModeColor(mode) {
      if (!mode) return 'success';
      const modeLower = mode.toLowerCase();
      if (modeLower === 'remove') return 'error';
      return 'success';
    },
    isRemoveMode(mode) {
      if (!mode) return false;
      return mode.toLowerCase() === 'remove';
    },
    getRecordsChangedClass(mode) {
      if (!mode) return 'success--text font-weight-medium';
      const modeLower = mode.toLowerCase();
      if (modeLower === 'remove') return 'error--text font-weight-medium';
      return 'success--text font-weight-medium';
    },
    truncateName(name, maxLength = 20) {
      if (!name) return '';
      if (name.length <= maxLength) return name;
      return name.substring(0, maxLength) + '…';
    },
    
    // Parse target items robustly by checking IDs for separators
    getTargetItems(item, type) {
      if (!item) return { displayItems: [], overflowCount: 0 };
      
      const idsStr = type === 'list' ? item.target_list_ids : item.target_bulk_message_ids;
      const namesStr = type === 'list' ? item.target_list_names : item.target_bulk_message_names;
      
      if (!idsStr) return { displayItems: [], overflowCount: 0 };
      
      // Determine separator based on IDs string (UUIDs don't contain | or ,)
      let separator = null;
      if (idsStr.indexOf('|') !== -1) separator = '|';
      else if (idsStr.indexOf(',') !== -1) separator = ',';
      
      let items = [];
      if (!separator) {
        // Single item case - use full name string
        items.push({ 
           id: idsStr.trim(), 
           name: namesStr ? namesStr.trim() : 'Unknown' 
        });
      } else {
        // Multiple items case
        const ids = idsStr.split(separator).map(s => s.trim()).filter(Boolean);
        const names = (namesStr || '').split(separator).map(s => s.trim()).filter(Boolean);
        
        items = ids.map((id, index) => ({
            id: id,
            name: names[index] || 'Unknown'
        }));
      }
      
      // Limit to 3 for display... This really doesn't come up in practice since we haven't exposed the ability to target multiple lists/bulk messages in the UI
      const limit = 3;
      const overflowCount = Math.max(0, items.length - limit);
      const displayItems = items.slice(0, limit).map(i => ({
          id: i.id,
          name: i.name,
          route: type === 'list' 
            ? { name: 'edit-list', params: { list_id: i.id } }
            : { name: 'view-bulk-message', params: { organization_id: this.$route.params.organization_id, bulk_message_id: i.id } }
      }));
      
      return { displayItems, overflowCount };
    },
    hasDetailsValue(details) {
      if (details === null || details === undefined || details === '') {
        return false;
      }
      if (Array.isArray(details)) {
        return details.length > 0;
      }
      if (typeof details === 'object') {
        return Object.keys(details).length > 0;
      }
      return true;
    },
    shouldShowActionsMenu(item) {
      if (!item) return false;
      return this.hasDetailsValue(item.import_data_source_details) || item.state !== 'completed';
    },
    setActionsMenuOpen(item, is_open) {
      if (!item) return;
      this.actions_menu_open_id = is_open ? item.import_id : null;
    },
    closeActionsMenu(item) {
      if (!item) return;
      if (this.actions_menu_open_id === item.import_id) {
        this.actions_menu_open_id = null;
      }
    },

    onClickViewPreview(import_item) {
      this.$router.push({
        name: this.upload_route_name,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        }
      });
    },
    onClickDownloadFile(import_item) {
      this.$rc.rest({
        spinner: true,
        endpoint: `GET: /api/v1/imports/${this.import_type}/download-file/:organization_id/:import_id`,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        },
        on_success: (data) => { window.open(data.download_url, '_blank'); },
        on_error: 'Unable to download import file'
      });
    },
    onClickDownloadEnrichedData(import_item) {
      this.$rc.rest({
        spinner: true,
        endpoint: `GET: /api/v1/imports/${this.import_type}/download-enriched-data/:organization_id/:import_id`,
        params: {
          organization_id: this.$route.params.organization_id,
          import_id: import_item.import_id
        },
        on_success: (data) => { window.open(data.download_url, '_blank'); },
        on_error: 'Unable to download enriched data file'
      });
    }
  },
  data() {
    return {
      actions_menu_open_id: null
    };
  }
};
</script>

<style scoped>
.target-lists-display,
.target-bulk-messages-display {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-width: 300px;
}

.clickable-chip {
  cursor: pointer;
}

.clickable-chip:hover {
  opacity: 0.85;
}

.records-changed-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Row hover effect for better scanability */
.enhanced-data-table--imports >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04) !important;
}
</style>
